import { Card, CardContent, CardHeader, CardTitle, LoadingCard } from '@folio/components';
import { ErrorMessage } from '@folio/components/module-elements/ErrorMessage';
import { useAssertedParams } from '@folio/lib';
import { useQuery } from '@tanstack/react-query';
import { claimManagementControllerGetQueryOptions } from 'korint-sdk';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { claimSectionItems } from '../ClaimInformationModule/formatClaimFieldValue';
import { type InformationItem, InformationList } from '../ClaimInformationModule/InformationList';

const INSURED_SECTION = 'assure';
const CLAIMANT_SECTION = 'declarant';

function PartyCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card role="region" aria-label={title}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function Muted({ children }: { children: ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

export function ClaimPartiesModule() {
  const { t, i18n } = useTranslation();
  const { claimId } = useAssertedParams<{ claimId: string }>();

  const {
    data: claim,
    isLoading,
    isError,
  } = useQuery(claimManagementControllerGetQueryOptions(claimId));

  if (isLoading) {
    return <LoadingCard />;
  }

  if (isError || !claim) {
    return <ErrorMessage>{t('claimInformation.error')}</ErrorMessage>;
  }

  const context = {
    locale: i18n.language,
    booleanLabels: { yes: t('options.true'), no: t('options.false') },
  };
  const partyDetails = (items: InformationItem[]) =>
    items.length > 0 ? (
      <InformationList items={items} />
    ) : (
      <Muted>{t('claimParties.noInformation')}</Muted>
    );

  return (
    <div className="flex flex-col gap-4">
      <PartyCard title={t('claimParties.insured')}>
        {partyDetails(claimSectionItems(claim.fields, INSURED_SECTION, context))}
      </PartyCard>
      <PartyCard title={t('claimParties.claimant')}>
        {claim.insureeIsClaimant ? (
          <Muted>{t('claimParties.insuredIsClaimant')}</Muted>
        ) : (
          partyDetails(claimSectionItems(claim.fields, CLAIMANT_SECTION, context))
        )}
      </PartyCard>
    </div>
  );
}
